import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';

// Mock data for invoices
const mockInvoices = [
  {
    id: 1,
    invoiceNumber: 'INV-2025-0001',
    orderId: 1,
    orderNumber: 'ORD-2025-0001',
    customerId: 1,
    customerName: 'Acme Corp',
    customerEmail: 'contact@acmecorp.com',
    customerAddress: '123 Business St, City',
    issueDate: '2025-05-01',
    dueDate: '2025-05-15',
    status: 'paid',
    paymentMethod: 'bank_transfer',
    paymentDate: '2025-05-10',
    notes: 'Payment received via bank transfer',
    items: [
      {
        id: 1,
        productId: 1,
        productName: 'Laptop',
        quantity: 2,
        unitPrice: 999.99,
        tax: 20,
        total: 2399.98,
      },
      {
        id: 2,
        productId: 3,
        productName: 'Printer Paper',
        quantity: 5,
        unitPrice: 24.99,
        tax: 20,
        total: 149.94,
      },
    ],
    subtotal: 2049.97,
    taxAmount: 410.00,
    total: 2459.97,
  },
  {
    id: 2,
    invoiceNumber: 'INV-2025-0002',
    orderId: 2,
    orderNumber: 'ORD-2025-0002',
    customerId: 2,
    customerName: 'TechStart Inc',
    customerEmail: 'info@techstart.com',
    customerAddress: '456 Tech Ave, Town',
    issueDate: '2025-05-03',
    dueDate: '2025-05-17',
    status: 'pending',
    paymentMethod: '',
    paymentDate: '',
    notes: 'Invoice sent via email',
    items: [
      {
        id: 1,
        productId: 2,
        productName: 'Smartphone',
        quantity: 2,
        unitPrice: 699.99,
        tax: 20,
        total: 1679.98,
      },
    ],
    subtotal: 1399.98,
    taxAmount: 280.00,
    total: 1679.98,
  },
];

// Mock data for customers
const mockCustomers = [
  { id: 1, name: 'Acme Corp', email: 'contact@acmecorp.com', phone: '123-456-7890', address: '123 Business St, City' },
  { id: 2, name: 'TechStart Inc', email: 'info@techstart.com', phone: '234-567-8901', address: '456 Tech Ave, Town' },
  { id: 3, name: 'Global Supplies Ltd', email: 'sales@globalsupplies.com', phone: '345-678-9012', address: '789 Supply Rd, County' },
  { id: 4, name: 'City Office Solutions', email: 'support@cityoffice.com', phone: '456-789-0123', address: '101 Office Blvd, City' },
];

// Mock data for orders
const mockOrders = [
  {
    id: 1,
    orderNumber: 'ORD-2025-0001',
    customerId: 1,
    customerName: 'Acme Corp',
    orderDate: '2025-05-01',
    status: 'completed',
    total: 2459.97,
  },
  {
    id: 2,
    orderNumber: 'ORD-2025-0002',
    customerId: 2,
    customerName: 'TechStart Inc',
    orderDate: '2025-05-03',
    status: 'completed',
    total: 1679.98,
  },
  {
    id: 3,
    orderNumber: 'ORD-2025-0003',
    customerId: 3,
    customerName: 'Global Supplies Ltd',
    orderDate: '2025-05-05',
    status: 'completed',
    total: 899.94,
  },
  {
    id: 4,
    orderNumber: 'ORD-2025-0004',
    customerId: 4,
    customerName: 'City Office Solutions',
    orderDate: '2025-05-07',
    status: 'completed',
    total: 1499.90,
  },
];

// Mock data for products
const mockProducts = [
  {
    id: 1,
    sku: 'ELEC-001',
    name: 'Laptop',
    unitPrice: 999.99,
    taxRate: 20,
  },
  {
    id: 2,
    sku: 'ELEC-002',
    name: 'Smartphone',
    unitPrice: 699.99,
    taxRate: 20,
  },
  {
    id: 3,
    sku: 'OFF-001',
    name: 'Printer Paper',
    unitPrice: 24.99,
    taxRate: 20,
  },
  {
    id: 4,
    sku: 'OFF-002',
    name: 'Ballpoint Pens',
    unitPrice: 12.99,
    taxRate: 20,
  },
];

interface InvoiceItem {
  id?: number;
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  tax: number;
  total: number;
}

interface FormData {
  invoiceNumber: string;
  orderId: number;
  orderNumber: string;
  customerId: number;
  customerName: string;
  customerEmail: string;
  customerAddress: string;
  issueDate: string;
  dueDate: string;
  status: string;
  paymentMethod: string;
  paymentDate: string;
  notes: string;
  items: InvoiceItem[];
  subtotal: number;
  taxAmount: number;
  total: number;
}

interface FormErrors {
  invoiceNumber?: string;
  orderId?: string;
  customerId?: string;
  issueDate?: string;
  dueDate?: string;
  status?: string;
  items?: string;
}

const InvoiceForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { formatCurrency } = useSettings();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState<FormData>({
    invoiceNumber: '',
    orderId: 0,
    orderNumber: '',
    customerId: 0,
    customerName: '',
    customerEmail: '',
    customerAddress: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'draft',
    paymentMethod: '',
    paymentDate: '',
    notes: '',
    items: [],
    subtotal: 0,
    taxAmount: 0,
    total: 0,
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch customers, orders, and products from the API
        const [customersResponse, ordersResponse, productsResponse] = await Promise.all([
          api.get('/customers'),
          api.get('/orders'),
          api.get('/products')
        ]);
        
        if (customersResponse.data && customersResponse.data.success) {
          setCustomers(customersResponse.data.data);
        } else {
          // Fallback to mock data if API fails
          setCustomers(mockCustomers);
        }
        
        if (ordersResponse.data && ordersResponse.data.success) {
          setOrders(ordersResponse.data.data);
        } else {
          // Fallback to mock data if API fails
          setOrders(mockOrders);
        }
        
        if (productsResponse.data && productsResponse.data.success) {
          setProducts(productsResponse.data.data);
        } else {
          // Fallback to mock data if API fails
          setProducts(mockProducts);
        }
        
        if (isEditMode) {
          // Fetch invoice data if in edit mode
          try {
            const invoiceResponse = await api.get(`/invoices/${id}`);
            
            if (invoiceResponse.data && invoiceResponse.data.success) {
              const invoiceData = invoiceResponse.data.data;
              
              // Use same defensive field access pattern as InvoiceDetail
              const customerName = invoiceData.customerName || invoiceData.customer_name || '';
              const customerEmail = invoiceData.customerEmail || invoiceData.customer_email || '';
              const customerAddress = invoiceData.customerAddress || invoiceData.customer_address || '';
              const issueDate = invoiceData.issueDate || invoiceData.issue_date || new Date().toISOString().split('T')[0];
              const dueDate = invoiceData.dueDate || invoiceData.due_date || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
              const status = invoiceData.status || 'draft';
              
              console.log('Invoice data loaded:', invoiceData);
              console.log('Customer name:', customerName);
              console.log('Issue date:', issueDate);
              console.log('Due date:', dueDate);
              console.log('Status:', status);
              
              // Set form data with defensive field access
              setFormData({
                invoiceNumber: invoiceData.invoiceNumber || invoiceData.invoice_number || '',
                orderId: invoiceData.orderId || invoiceData.order_id || 0,
                orderNumber: invoiceData.orderNumber || invoiceData.order_number || '',
                customerId: invoiceData.customerId || invoiceData.customer_id || 0,
                customerName: customerName,
                customerEmail: customerEmail,
                customerAddress: customerAddress,
                issueDate: issueDate,
                dueDate: dueDate,
                status: status,
                paymentMethod: invoiceData.paymentMethod || invoiceData.payment_method || '',
                paymentDate: invoiceData.paymentDate || invoiceData.payment_date || '',
                notes: invoiceData.notes || '',
                items: (invoiceData.items || []).map((item: any) => ({
                  id: item.id,
                  productId: item.productId || item.product_id,
                  productName: item.productName || item.product_name,
                  quantity: Number(item.quantity),
                  unitPrice: Number(item.unitPrice || item.unit_price),
                  tax: Number(item.tax || item.tax_rate),
                  total: Number(item.total)
                })),
                subtotal: Number(invoiceData.subtotal),
                taxAmount: Number(invoiceData.taxAmount || invoiceData.tax_amount),
                total: Number(invoiceData.total || invoiceData.total_amount),
              });
              
              // Create customer object for Autocomplete from the invoice data itself
              const customerFromInvoice = {
                id: invoiceData.customerId || invoiceData.customer_id || 0,
                name: customerName,
                company_name: customerName, // Backend expects company_name
                email: customerEmail,
                address: customerAddress,
                customer_name: customerName // Also add the snake_case version
              };
              
              // Set customer and order selection from invoice data
              console.log('Setting customer from invoice data:', customerFromInvoice);
              
              // Always set the selected customer from the invoice data 
              setSelectedCustomer(customerFromInvoice);
              
              // Add the customer to the customers list if it's not already there
              setCustomers(prevCustomers => {
                const existingCustomer = prevCustomers.find(c => c.id === customerFromInvoice.id);
                if (!existingCustomer && customerFromInvoice.id > 0) {
                  console.log('Adding customer to dropdown list:', customerFromInvoice);
                  return [...prevCustomers, customerFromInvoice];
                }
                return prevCustomers;
              });
              
              // Set selected order if we have order data
              const orderData = ordersResponse.data && ordersResponse.data.success ? 
                ordersResponse.data.data.find((o: any) => o.id === (invoiceData.order_id || invoiceData.orderId)) : null;
              
              if (orderData) {
                console.log('Found and setting order data:', orderData);
                setSelectedOrder(orderData);
              } else if (invoiceData.order_id || invoiceData.orderId) {
                // Create order object from invoice data if not found in orders list
                const orderFromInvoice = {
                  id: invoiceData.order_id || invoiceData.orderId,
                  orderNumber: invoiceData.order_number || invoiceData.orderNumber || '',
                  customer_id: invoiceData.customer_id || invoiceData.customerId,
                  customerName: customerName
                };
                console.log('Creating order from invoice data:', orderFromInvoice);
                setSelectedOrder(orderFromInvoice);
                
                // Add to orders list if not there
                setOrders(prevOrders => {
                  const existingOrder = prevOrders.find(o => o.id === orderFromInvoice.id);
                  if (!existingOrder) {
                    return [...prevOrders, orderFromInvoice];
                  }
                  return prevOrders;
                });
              }
            } else {
              // Fallback to mock data if API fails
              const mockInvoiceData = mockInvoices.find(i => i.id === Number(id));
              
              if (mockInvoiceData) {
                setFormData({
                  invoiceNumber: mockInvoiceData.invoiceNumber,
                  orderId: mockInvoiceData.orderId,
                  orderNumber: mockInvoiceData.orderNumber,
                  customerId: mockInvoiceData.customerId,
                  customerName: mockInvoiceData.customerName,
                  customerEmail: mockInvoiceData.customerEmail,
                  customerAddress: mockInvoiceData.customerAddress,
                  issueDate: mockInvoiceData.issueDate,
                  dueDate: mockInvoiceData.dueDate,
                  status: mockInvoiceData.status,
                  paymentMethod: mockInvoiceData.paymentMethod,
                  paymentDate: mockInvoiceData.paymentDate,
                  notes: mockInvoiceData.notes,
                  items: mockInvoiceData.items,
                  subtotal: mockInvoiceData.subtotal,
                  taxAmount: mockInvoiceData.taxAmount,
                  total: mockInvoiceData.total,
                });
                
                setSelectedOrder(mockOrders.find(o => o.id === mockInvoiceData.orderId) || null);
                setSelectedCustomer(mockCustomers.find(c => c.id === mockInvoiceData.customerId) || null);
              }
            }
          } catch (error) {
            console.error('Error fetching invoice data:', error);
            // Show error message to user
            setSubmitError('Failed to load invoice data. Please try again.');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (!authLoading && isAuthenticated) {
      fetchData();
    }
  }, [authLoading, isAuthenticated, id, isEditMode]);
  
  // Handle both input changes and select changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | any) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value,
      });
      
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined,
        });
      }
    }
  };
  
  const handleOrderChange = (event: any, newValue: any) => {
    setSelectedOrder(newValue);
    if (newValue) {
      // Find the customer for this order
      const customer = customers.find(c => c.id === newValue.customerId);
      setSelectedCustomer(customer || null);
      
      setFormData({
        ...formData,
        orderId: newValue.id,
        orderNumber: newValue.orderNumber,
        customerId: newValue.customerId,
        customerName: newValue.customerName,
        customerEmail: customer?.email || '',
        customerAddress: customer?.address || '',
        // We would fetch order items here in a real application
        // For now, we'll leave items empty
        items: [],
      });
    } else {
      setSelectedCustomer(null);
      setFormData({
        ...formData,
        orderId: 0,
        orderNumber: '',
        customerId: 0,
        customerName: '',
        customerEmail: '',
        customerAddress: '',
        items: [],
      });
    }
  };
  
  const handleCustomerChange = (event: any, newValue: any) => {
    setSelectedCustomer(newValue);
    if (newValue) {
      setFormData({
        ...formData,
        customerId: newValue.id,
        customerName: newValue.name,
        customerEmail: newValue.email,
        customerAddress: newValue.address,
      });
    } else {
      setFormData({
        ...formData,
        customerId: 0,
        customerName: '',
        customerEmail: '',
        customerAddress: '',
      });
    }
  };
  
  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          productId: 0,
          productName: '',
          quantity: 1,
          unitPrice: 0,
          tax: 20,
          total: 0,
        },
      ],
    });
  };
  
  const handleRemoveItem = (index: number) => {
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    
    const { subtotal, taxAmount, total } = calculateTotals(updatedItems);
    
    setFormData({
      ...formData,
      items: updatedItems,
      subtotal,
      taxAmount,
      total,
    });
  };
  
  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    const updatedItems = [...formData.items];
    
    if (field === 'productId') {
      const product = products.find(p => p.id === value);
      if (product) {
        updatedItems[index] = {
          ...updatedItems[index],
          productId: product.id,
          productName: product.name,
          unitPrice: product.unitPrice,
          tax: product.taxRate,
        };
      }
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };
    }
    
    // Recalculate item total
    const item = updatedItems[index];
    item.total = item.quantity * item.unitPrice * (1 + item.tax / 100);
    
    // Recalculate invoice totals
    const { subtotal, taxAmount, total } = calculateTotals(updatedItems);
    
    setFormData({
      ...formData,
      items: updatedItems,
      subtotal,
      taxAmount,
      total,
    });
  };
  
  const calculateTotals = (items: InvoiceItem[]) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    const taxAmount = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice * (item.tax / 100)), 0);
    const total = subtotal + taxAmount;
    
    return { subtotal, taxAmount, total };
  };
  
  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    return `INV-${year}-${month}${day}-${random}`;
  };
  
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.invoiceNumber.trim()) {
      newErrors.invoiceNumber = 'Invoice number is required';
    }
    
    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }
    
    if (!formData.issueDate) {
      newErrors.issueDate = 'Issue date is required';
    }
    
    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }
    
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }
    
    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    setSubmitError(null);
    
    try {
      // Prepare the data for API submission
      const apiData = {
        invoice_number: formData.invoiceNumber,
        order_id: formData.orderId,
        customer_id: formData.customerId,
        issue_date: formData.issueDate,
        due_date: formData.dueDate,
        status: formData.status,
        payment_method: formData.paymentMethod,
        payment_date: formData.paymentDate,
        notes: formData.notes,
        items: formData.items.map(item => ({
          id: item.id,
          product_id: item.productId,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          tax_rate: item.tax,
          total: item.total
        })),
        subtotal: formData.subtotal,
        tax_amount: formData.taxAmount,
        total_amount: formData.total
      };
      
      // Call the API to save the invoice
      let response;
      if (isEditMode) {
        response = await api.put(`/invoices/${id}`, apiData);
      } else {
        response = await api.post('/invoices', apiData);
      }
      
      if (response.data && response.data.success) {
        // Navigate back to invoices list on success
        navigate('/invoices');
      } else {
        // Handle API error
        setSubmitError(response.data?.message || 'Failed to save invoice. Please try again.');
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      setSubmitError('An error occurred while saving the invoice. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/invoices');
  };
  
  const handleGenerateInvoiceNumber = () => {
    setFormData({
      ...formData,
      invoiceNumber: generateInvoiceNumber(),
    });
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4">
          {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
        </Typography>
      </Box>
      
      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Invoice Details
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Invoice Number"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.invoiceNumber}
                  helperText={errors.invoiceNumber}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleGenerateInvoiceNumber}
                  sx={{ minWidth: '120px' }}
                >
                  Generate
                </Button>
              </Box>
            </Box>
            
            <Box>
              <Autocomplete
                options={orders}
                getOptionLabel={(option) => option ? `${option.orderNumber || option.order_number} - ${option.customerName || option.customer_name}` : ''}
                value={selectedOrder}
                onChange={handleOrderChange}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Related Order"
                    fullWidth
                  />
                )}
              />
            </Box>
            
            <Box>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => option ? (option.name || option.customer_name || '') : ''}
                value={selectedCustomer}
                onChange={handleCustomerChange}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer"
                    required
                    error={!!errors.customerId}
                    helperText={errors.customerId}
                    fullWidth
                  />
                )}
              />
            </Box>
            
            <Box>
              <TextField
                label="Customer Email"
                name="customerEmail"
                value={formData.customerEmail}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                label="Customer Address"
                name="customerAddress"
                value={formData.customerAddress}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, gridColumn: '1 / -1' }}>
              <Box>
                <TextField
                  label="Issue Date"
                  name="issueDate"
                  type="date"
                  value={formData.issueDate || ''}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.issueDate}
                  helperText={errors.issueDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              
              <Box>
                <TextField
                  label="Due Date"
                  name="dueDate"
                  type="date"
                  value={formData.dueDate || ''}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.dueDate}
                  helperText={errors.dueDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              
              <Box>
                <FormControl fullWidth required error={!!errors.status}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status || 'draft'}
                    onChange={handleChange}
                    label="Status"
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                  {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
                </FormControl>
              </Box>
            </Box>
            
            {formData.status === 'paid' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, gridColumn: '1 / -1' }}>
                <Box>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleChange}
                      label="Payment Method"
                    >
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="credit_card">Credit Card</MenuItem>
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                      <MenuItem value="check">Check</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                <Box>
                  <TextField
                    label="Payment Date"
                    name="paymentDate"
                    type="date"
                    value={formData.paymentDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>
            )}
            
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          </Box>
        </Paper>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Invoice Items
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
            >
              Add Item
            </Button>
          </Box>
          
          {errors.items && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.items}
            </Alert>
          )}
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Tax (%)</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Autocomplete
                        options={products}
                        getOptionLabel={(option) => `${option.name} (${option.sku})`}
                        value={products.find(p => p.id === item.productId) || null}
                        onChange={(event, newValue) => {
                          handleItemChange(index, 'productId', newValue?.id || 0);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value))}
                        inputProps={{ min: 1 }}
                        size="small"
                        sx={{ width: '80px' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, 'unitPrice', Number(e.target.value))}
                        inputProps={{ min: 0, step: 0.01 }}
                        size="small"
                        sx={{ width: '100px' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={item.tax}
                        onChange={(e) => handleItemChange(index, 'tax', Number(e.target.value))}
                        inputProps={{ min: 0, max: 100 }}
                        size="small"
                        sx={{ width: '80px' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.total)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                
                {formData.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No items added to this invoice
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Invoice Summary
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '300px' }}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1">{formatCurrency(formData.subtotal)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '300px' }}>
              <Typography variant="body1">Tax:</Typography>
              <Typography variant="body1">{formatCurrency(formData.taxAmount)}</Typography>
            </Box>
            <Divider sx={{ width: '300px', my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '300px' }}>
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6">{formatCurrency(formData.total)}</Typography>
            </Box>
          </Box>
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={submitLoading}
          >
            {submitLoading ? 'Saving...' : (isEditMode ? 'Update Invoice' : 'Create Invoice')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceForm;
